/**
 * Ragdoll physics proof-of-concept.
 *
 * Stack: melonJS (rendering, game loop, input) + @melonjs/planck-adapter
 * (Box2D-class rigid-body physics with joints). The point of this POC is to
 * prove out the toolchain end to end — we never hand-roll a physics engine.
 *
 * A ragdoll is built from a handful of invisible physics bodies (Part) joined
 * at the neck/shoulders/elbows/hips/knees with planck RevoluteJoints. A single
 * Painter renderable draws every body by reading its live position + angle, so
 * we sidestep melonJS's per-renderable anchor/transform bookkeeping entirely.
 * Drag any limb with the mouse/finger (implemented with a planck MouseJoint).
 */
import { Application, Renderable, Rect, video } from "melonjs";
import { PlanckAdapter } from "@melonjs/planck-adapter";

const W = 960;
const H = 600;

const COLORS = {
  head: "#ffd9a0",
  torso: "#e622c5", // inkegg pink (was blue)
  arm: "#f0705a",
  leg: "#37c98b",
  ground: "#3a2f4a",
  wall: "#3a2f4a",
};

/**
 * An invisible rigid body. Drawing is done by the Painter, so draw() is a
 * no-op here — this class exists purely to declare a physics body via bodyDef.
 */
class Part extends Renderable {
  // x, y are the CENTRE of the part in pixels (converted to top-left for melonJS).
  constructor(cx, cy, w, h, color, type = "dynamic") {
    super(cx - w / 2, cy - h / 2, w, h);
    this.w = w;
    this.h = h;
    this.hw = w / 2;
    this.hh = h / 2;
    this.color = color;
    this.bodyDef = {
      type,
      shapes: [new Rect(0, 0, w, h)],
      fixedRotation: false, // adapter default is TRUE — must opt in to rotation
      density: 1.1,
      friction: 0.6,
      restitution: 0.05,
    };
  }

  draw() {
    /* invisible — the Painter renders every body */
  }
}

/**
 * Draws the whole scene each frame by reading each body's synced pos + angle.
 * anchorPoint is pinned to (0,0) and pos to (0,0) so our draw calls use
 * absolute canvas coordinates with no hidden offset.
 */
class Painter extends Renderable {
  constructor(parts, onTick) {
    super(0, 0, W, H);
    this.anchorPoint.set(0, 0);
    this.parts = parts;
    this.onTick = onTick;
    this.floating = true;
  }

  update(dt) {
    if (this.onTick) this.onTick(dt); // dt in ms
    return true; // always redraw (bodies move every frame)
  }

  draw(renderer) {
    // sky
    renderer.setColor("#0f1424");
    renderer.fillRect(0, 0, W, H);

    for (const p of this.parts) {
      // pos is written back by the adapter as the top-left corner each step.
      const cx = p.pos.x + p.hw;
      const cy = p.pos.y + p.hh;
      const angle = p.body ? p.body.getAngle() : 0;

      renderer.save();
      renderer.translate(cx, cy);
      renderer.rotate(angle);
      renderer.setColor(p.color);
      renderer.fillRect(-p.hw, -p.hh, p.w, p.h);
      renderer.restore();
    }
  }
}

function boot() {
  const app = new Application(W, H, {
    parent: "screen",
    renderer: video.AUTO,
    physic: new PlanckAdapter({ gravity: { x: 0, y: 900 } }),
  });
  app.isAlwaysDirty = true;
  // keep simulating even when the canvas/tab isn't focused (physics toy)
  app.pauseOnBlur = false;
  app.resumeOnFocus = true;
  app.stopOnBlur = false;

  const adapter = app.world.adapter;
  const planck = adapter.planck;
  const PPM = adapter.pixelsPerMeter;
  const toMeters = (px, py) => new planck.Vec2(px / PPM, py / PPM);

  const parts = []; // all drawn bodies (static enclosure + every ragdoll part)
  const dynParts = []; // dynamic ragdoll parts only
  const joints = []; // every revolute joint (across all ragdolls)
  const torsos = []; // each ragdoll's torso (for the spawn nudge / reset)

  const add = (cx, cy, w, h, color, type = "dynamic") => {
    const p = new Part(cx, cy, w, h, color, type);
    p.initCx = cx;
    p.initCy = cy;
    parts.push(p);
    if (type === "dynamic") dynParts.push(p);
    app.world.addChild(p);
    return p;
  };

  const pin = (a, b, px, py) => {
    const joint = new planck.RevoluteJoint(
      { collideConnected: false },
      a.body,
      b.body,
      toMeters(px, py),
    );
    adapter.world.createJoint(joint);
    joints.push(joint);
    return joint;
  };

  // --- static enclosure (added once, always drawn) ---
  const ground = add(W / 2, H - 12, W, 24, COLORS.ground, "static");
  add(6, H / 2, 24, H, COLORS.wall, "static"); // left wall
  add(W - 6, H / 2, 24, H, COLORS.wall, "static"); // right wall
  add(W / 2, 12, W, 24, COLORS.wall, "static"); // ceiling

  // Build one ragdoll centred at cx0. The body/joint layout is authored around
  // x=480; dx shifts the whole figure so several can spawn side by side.
  const buildRagdoll = (cx0) => {
    const dx = cx0 - 480;
    const B = (cx, cy, w, h, color) => add(cx + dx, cy, w, h, color);
    const head = B(480, 110, 44, 44, COLORS.head);
    const torso = B(480, 190, 54, 96, COLORS.torso);
    const uArmL = B(446, 175, 18, 58, COLORS.arm);
    const lArmL = B(446, 232, 16, 52, COLORS.arm);
    const uArmR = B(514, 175, 18, 58, COLORS.arm);
    const lArmR = B(514, 232, 16, 52, COLORS.arm);
    const thighL = B(466, 285, 22, 64, COLORS.leg);
    const shinL = B(466, 350, 20, 60, COLORS.leg);
    const thighR = B(494, 285, 22, 64, COLORS.leg);
    const shinR = B(494, 350, 20, 60, COLORS.leg);
    const J = (a, b, px, py) => pin(a, b, px + dx, py);
    J(head, torso, 480, 135); // neck
    J(torso, uArmL, 458, 150); // left shoulder
    J(uArmL, lArmL, 446, 203); // left elbow
    J(torso, uArmR, 502, 150); // right shoulder
    J(uArmR, lArmR, 514, 203); // right elbow
    J(torso, thighL, 468, 250); // left hip
    J(thighL, shinL, 466, 318); // left knee
    J(torso, thighR, 492, 250); // right hip
    J(thighR, shinR, 494, 318); // right knee
    torsos.push(torso);
  };

  // Nudge each torso so ragdolls don't drop dead-straight.
  const nudgeAll = () => {
    for (const t of torsos) t.body.setAngularVelocity(2.5);
  };

  // Tear down all current ragdolls (removeChildNow destroys each body + its
  // joints synchronously); keep the static enclosure.
  const clearRagdolls = () => {
    release();
    for (const p of dynParts) app.world.removeChildNow(p);
    dynParts.length = 0;
    joints.length = 0;
    torsos.length = 0;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i].bodyDef.type === "dynamic") parts.splice(i, 1);
    }
  };

  // assigned later (in the controls section); spawnRagdolls calls it after a rebuild
  let reapplyBodyControls = () => {};

  const MAX_RAGDOLLS = 6;
  const spawnRagdolls = (n) => {
    clearRagdolls();
    n = Math.max(1, Math.min(MAX_RAGDOLLS, Math.round(n)));
    for (let i = 0; i < n; i++) {
      const cx = n === 1 ? W / 2 : 150 + ((W - 300) * i) / (n - 1);
      buildRagdoll(cx);
    }
    reapplyBodyControls(); // stiffness / bounce / limits onto the new joints+bodies
    nudgeAll();
  };

  // expose for debugging / automated verification
  window.ragdollApp = app;
  window.ragdollParts = parts;
  window.ragdollJoints = joints;

  // per-frame hook (assigned in the Objects/blast section); receives dt in ms
  let onFrame = () => {};
  app.world.addChild(new Painter(parts, (dt) => onFrame(dt)), 100);

  // --- drag any limb with the pointer (planck MouseJoint) ---
  let mouseJoint = null;
  let dragForce = 3000; // per-unit-mass MouseJoint force; set by the slider
  const canvas = app.renderer.getCanvas();

  // Convert a DOM pointer position to game-world pixels. Uses the canvas's
  // displayed size (getBoundingClientRect) vs its render resolution (W×H), so
  // it stays correct even though CSS stretches the canvas inside #screen.
  const toWorldPx = (ev) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((ev.clientX - rect.left) / rect.width) * W,
      y: ((ev.clientY - rect.top) / rect.height) * H,
    };
  };

  canvas.addEventListener("pointerdown", (ev) => {
    const { x: px, y: py } = toWorldPx(ev);
    const hits = adapter.queryAABB(new Rect(px - 6, py - 6, 12, 12));
    const target = hits.find((r) => r.bodyDef && r.bodyDef.type === "dynamic");
    if (!target) return;
    mouseJoint = new planck.MouseJoint(
      { maxForce: dragForce * target.body.getMass(), frequencyHz: 5, dampingRatio: 0.8 },
      ground.body,
      target.body,
      toMeters(px, py),
    );
    adapter.world.createJoint(mouseJoint);
    canvas.setPointerCapture?.(ev.pointerId);
  });

  // move/up on window so a drag keeps working past the canvas edge
  window.addEventListener("pointermove", (ev) => {
    if (!mouseJoint) return;
    const { x, y } = toWorldPx(ev);
    mouseJoint.setTarget(toMeters(x, y));
  });

  const release = () => {
    if (mouseJoint) {
      adapter.world.destroyJoint(mouseJoint);
      mouseJoint = null;
    }
  };
  window.addEventListener("pointerup", release);
  window.addEventListener("pointercancel", release);

  // Respawn = rebuild the ragdolls at their start pose. A full rebuild (rather
  // than an in-place transform reset) so joints snapped by a blast are restored.
  // Slider settings persist (spawnRagdolls re-applies them).
  const respawn = () => spawnRagdolls(numVal("rd-spawn", 1));
  const respawnBtn = document.getElementById("ragdoll-respawn");
  const flashRespawn = () => {
    if (!respawnBtn) return;
    respawnBtn.classList.remove("flash");
    void respawnBtn.offsetWidth; // restart the animation
    respawnBtn.classList.add("flash");
  };
  respawnBtn?.addEventListener("click", () => { respawn(); flashRespawn(); });
  window.addEventListener("keydown", (ev) => {
    // ignore R while typing in an input (e.g. a slider or the EXPLORE search box)
    const t = ev.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
    if (ev.key === "r" || ev.key === "R") { respawn(); flashRespawn(); }
  });

  // ── Physics controls ──────────────────────────────────────────────────────
  const $ = (id) => document.getElementById(id);

  // Joint stiffness: drive every joint's motor toward 0 speed. maxMotorTorque is
  // the stiffness — 0 disables the motor (fully floppy), high values hold pose.
  const applyStiffness = (v) => {
    for (const j of joints) {
      if (v <= 0) {
        j.enableMotor(false);
      } else {
        j.enableMotor(true);
        j.setMotorSpeed(0);
        j.setMaxMotorTorque(v);
      }
    }
  };

  // Gravity in px/s² — mutate the adapter's gravity vector (it re-applies to the
  // planck world every step, so setting world gravity directly wouldn't stick).
  const applyGravity = (v) => adapter.gravity.set(0, v);

  const applyBounce = (v) => {
    for (const p of dynParts) {
      if (typeof p.body.setBounce === "function") {
        p.body.setBounce(v); // adapter-spliced helper (sets every fixture)
      } else {
        for (let f = p.body.getFixtureList(); f; f = f.getNext()) f.setRestitution(v);
      }
    }
  };

  // Joint angle limits, ± around each joint's rest angle.
  const applyLimits = (on, deg) => {
    const a = (deg * Math.PI) / 180;
    for (const j of joints) {
      j.enableLimit(on);
      if (on) j.setLimits(-a, a);
    }
  };

  const wire = (id, fn, fmt) => {
    const el = $(id);
    const out = $(id + "-val");
    if (!el) return;
    const run = () => {
      const num = parseFloat(el.value);
      if (out && fmt) out.textContent = fmt(num);
      fn(num);
    };
    el.addEventListener("input", run);
    run(); // apply the default on load
  };

  wire("rd-stiffness", applyStiffness, (n) => (n <= 0 ? "floppy" : String(n)));
  wire("rd-gravity", applyGravity, (n) => n + " px/s²");
  wire("rd-bounce", applyBounce, (n) => n.toFixed(2));
  wire("rd-drag", (n) => { dragForce = n; }, (n) => String(n));

  // Joint-limit toggle + angle (coupled).
  const limitOn = $("rd-limit-on");
  const limitAngle = $("rd-limit-angle");
  const limitAngleVal = $("rd-limit-angle-val");
  const limitWrap = $("rd-limit-angle-wrap");
  const applyLimitControls = () => {
    const on = !!(limitOn && limitOn.checked);
    const deg = limitAngle ? parseFloat(limitAngle.value) : 60;
    if (limitAngleVal) limitAngleVal.textContent = deg + "°";
    if (limitWrap) limitWrap.classList.toggle("is-disabled", !on);
    if (limitAngle) limitAngle.disabled = !on;
    applyLimits(on, deg);
  };
  if (limitOn) limitOn.addEventListener("change", applyLimitControls);
  if (limitAngle) limitAngle.addEventListener("input", applyLimitControls);
  applyLimitControls();

  // Re-apply the per-body/joint settings (stiffness, bounce, limits) after a
  // rebuild. Gravity and drag are global, so they persist without re-applying.
  const numVal = (id, d) => { const el = $(id); return el ? parseFloat(el.value) : d; };
  reapplyBodyControls = () => {
    applyStiffness(numVal("rd-stiffness", 0));
    applyBounce(numVal("rd-bounce", 0.05));
    applyLimitControls();
  };

  // Number of ragdolls. wire()'s immediate run() performs the initial spawn.
  wire("rd-spawn", (n) => spawnRagdolls(n), (n) => String(Math.round(n)));

  // Reset controls to their default values and re-apply.
  $("rd-reset")?.addEventListener("click", () => {
    const defs = {
      "rd-stiffness": "0",
      "rd-gravity": "150",
      "rd-bounce": "0.05",
      "rd-drag": "3000",
      "rd-limit-angle": "40",
      "rd-spawn": "1",
    };
    for (const id in defs) { const el = $(id); if (el) el.value = defs[id]; }
    if (limitOn) limitOn.checked = true;
    // global + label updates first, then respawn (which re-applies body settings)
    ["rd-stiffness", "rd-gravity", "rd-bounce", "rd-drag", "rd-spawn"].forEach((id) =>
      $(id)?.dispatchEvent(new Event("input", { bubbles: true })),
    );
    applyLimitControls();
  });

  // ── Objects: the Blast — a gravity-well eruption that can snap joints ──────
  const blast = { strength: 180, radius: 800, bond: 500, explode: false };
  let blastTimer = 0; // seconds left in the active eruption
  const BLAST_DURATION = 0.5;

  const triggerBlast = () => {
    blast.strength = numVal("obj-blast-strength", blast.strength);
    blast.radius = numVal("obj-blast-radius", blast.radius);
    blast.bond = numVal("obj-blast-bond", blast.bond);
    blast.explode = !!($("obj-blast-explode") && $("obj-blast-explode").checked);
    blastTimer = BLAST_DURATION;
  };

  // Runs every frame (via the Painter). While the blast is active, pull (or push)
  // every body toward/from the arena centre, then snap any joint whose constraint
  // force exceeds the bond threshold.
  onFrame = (dtMs) => {
    if (blastTimer <= 0) return;
    const dt = Math.min(dtMs / 1000, 0.05);
    blastTimer -= dt;

    const cx = W / 2 / PPM;
    const cy = H / 2 / PPM;
    const rM = blast.radius / PPM;
    const sign = blast.explode ? -1 : 1;

    for (const p of dynParts) {
      const b = p.body;
      const pos = b.getWorldCenter();
      const dx = cx - pos.x;
      const dy = cy - pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist > rM || dist < 1e-4) continue;
      const fall = Math.max(0, 1 - dist / rM); // strongest at the centre
      const accel = sign * blast.strength * (0.25 + 0.75 * fall);
      const mag = accel * b.getMass(); // force = m·a ⇒ uniform accel
      b.applyForceToCenter(new planck.Vec2((dx / dist) * mag, (dy / dist) * mag), true);
    }

    const invDt = 1 / dt;
    for (let i = joints.length - 1; i >= 0; i--) {
      const rf = joints[i].getReactionForce(invDt);
      if (Math.hypot(rf.x, rf.y) > blast.bond) {
        adapter.world.destroyJoint(joints[i]);
        joints.splice(i, 1);
      }
    }
  };

  $("obj-blast-trigger")?.addEventListener("click", triggerBlast);

  const objLabel = (id, fmt) => {
    const el = $(id);
    const out = $(id + "-val");
    if (!el) return;
    const run = () => { if (out) out.textContent = fmt(parseFloat(el.value)); };
    el.addEventListener("input", run);
    run();
  };
  objLabel("obj-blast-strength", (n) => String(n));
  objLabel("obj-blast-radius", (n) => n + " px");
  objLabel("obj-blast-bond", (n) => String(n));

  // ── Options overlay: rail buttons show/hide panels over the stage ──────────
  const overlay = $("rd-overlay");
  const railButtons = Array.from(document.querySelectorAll(".rd-rail-btn[data-panel]"));
  const overlayPanels = Array.from(document.querySelectorAll("[data-panel-content]"));
  let openPanel = null;

  const showPanel = (name) => {
    openPanel = name;
    if (overlay) overlay.hidden = false;
    overlayPanels.forEach((s) => { s.hidden = s.getAttribute("data-panel-content") !== name; });
    railButtons.forEach((b) => b.classList.toggle("is-active", b.getAttribute("data-panel") === name));
  };
  const closeOverlay = () => {
    openPanel = null;
    if (overlay) overlay.hidden = true;
    railButtons.forEach((b) => b.classList.remove("is-active"));
  };

  railButtons.forEach((b) =>
    b.addEventListener("click", () => {
      const name = b.getAttribute("data-panel");
      if (openPanel === name) closeOverlay();
      else showPanel(name);
    }),
  );
  document.querySelectorAll("[data-close]").forEach((el) => el.addEventListener("click", closeOverlay));
  window.addEventListener("keydown", (ev) => { if (ev.key === "Escape") closeOverlay(); });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
